import type { Vec2 } from "../core/types.ts";
import { CELL_SIZE } from "../core/config.ts";
import type { WaveManager } from "./WaveManager.ts";

const LOCAL_REROUTE_MAX_DIST = CELL_SIZE * 1.5;

interface PathRepathLookup {
  findPixelPathFrom(id: string, startPixel: Vec2): Vec2[] | null;
}

function distanceSquared(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Reroute all active enemies onto the new path after a placement.
 * Uses forward-only heuristic with hysteresis: only waypoints that represent
 * forward progress (or < 1 cell backtrack) are considered as candidates.
 * Fallback: enemy keeps old path remainder; if old path is empty, teleport.
 * @internal Exported for testing only.
 */
export function rerouteActiveEnemies(
  waveManager: WaveManager,
  newPath: Vec2[],
  spawnPathId: string = "primary",
  repathLookup?: PathRepathLookup,
): void {
  if (newPath.length === 0) return;

  // Pre-compute exit position (last waypoint on new path)
  const exit = newPath[newPath.length - 1]!;

  for (const enemy of waveManager.enemies) {
    if (!enemy.active) continue;
    // CR-H6: only reroute enemies that spawned on THIS path. Without
    // this filter, breach-spawn enemies got snapped onto primary paths
    // (and vice versa) every time a tower was placed.
    if (enemy.spawnPathId !== spawnPathId) continue;

    // Current enemy distance to exit
    const edx = exit.x - enemy.x;
    const edy = exit.y - enemy.y;
    const enemyDistToExit = Math.sqrt(edx * edx + edy * edy);

    // Hysteresis threshold: allow up to 1 cell of "backtrack"
    const threshold = enemyDistToExit + CELL_SIZE;

    // Find best forward-only candidate
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < newPath.length; i++) {
      const wp = newPath[i]!;

      // Check if this waypoint represents forward progress (or within hysteresis)
      const wdx = exit.x - wp.x;
      const wdy = exit.y - wp.y;
      const wpDistToExit = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wpDistToExit > threshold) continue; // Too far back — skip

      // Distance from enemy to this waypoint
      const dx = wp.x - enemy.x;
      const dy = wp.y - enemy.y;
      const d = dx * dx + dy * dy;
      if (
        d <= LOCAL_REROUTE_MAX_DIST * LOCAL_REROUTE_MAX_DIST &&
        d < bestDist
      ) {
        bestDist = d;
        bestIdx = i;
      }
    }

    if (bestIdx >= 0) {
      // Found a valid forward waypoint — assign new path
      enemy.path = [...newPath];
      const anchor = newPath[bestIdx]!;
      enemy.pathIndex =
        distanceSquared(enemy, anchor) <= 1
          ? Math.min(bestIdx + 1, newPath.length - 1)
          : bestIdx;
      continue;
    }

    // No safe local segment on the new full path: repath from the enemy's
    // current cell to the exit and use that route DIRECTLY (it is continuous
    // enemy→exit). M1: do NOT stitch a stale traversed prefix onto it — that
    // seam could be discontinuous and made PID_HIJACK jump when walking back.
    const suffix = repathLookup?.findPixelPathFrom(spawnPathId, {
      x: enemy.x,
      y: enemy.y,
    });
    if (suffix && suffix.length > 0) {
      enemy.path = [...suffix];
      // suffix[0] is the enemy's own cell — advance to the next waypoint.
      enemy.pathIndex = Math.min(1, suffix.length - 1);
      continue;
    }

    // Last-resort (repath failed, e.g. the enemy's current cell is now blocked):
    // snap to the nearest FORWARD waypoint on the new path — NEVER index 0.
    // H2: index 0 walked the enemy all the way back to the entry/spawn. Forward
    // = no farther from the exit than the enemy already is (+1 cell hysteresis).
    let fbIdx = -1;
    let fbDist = Infinity;
    for (let i = 0; i < newPath.length; i++) {
      const wp = newPath[i]!;
      const wpDist = Math.hypot(exit.x - wp.x, exit.y - wp.y);
      if (wpDist > enemyDistToExit + CELL_SIZE) continue; // forward-only
      const d = distanceSquared(enemy, wp);
      if (d < fbDist) {
        fbDist = d;
        fbIdx = i;
      }
    }
    if (fbIdx < 0) {
      // Nothing forward (enemy behind the entry — shouldn't happen): nearest cell.
      for (let i = 0; i < newPath.length; i++) {
        const d = distanceSquared(enemy, newPath[i]!);
        if (d < fbDist) {
          fbDist = d;
          fbIdx = i;
        }
      }
    }
    if (fbIdx >= 0) {
      enemy.path = [...newPath];
      enemy.pathIndex = Math.min(fbIdx + 1, newPath.length - 1);
    }
  }
}

/** CR-H6 (LD-22.1-4): reroute ALL registered paths' enemies after a
 *  grid mutation. Iterates PathRegistry, calls rerouteActiveEnemies
 *  once per path id with the matching enemy filter. This replaces the
 *  old single-call pattern that only ever updated primary-spawn enemies
 *  (Bug 4: breach-secondary enemies kept walking stale paths through
 *  freshly placed towers). */
export function rerouteAllPaths(
  waveManager: WaveManager,
  registry: {
    getAllPathEntries(): Array<{ id: string; pixels: Vec2[] }>;
    findPixelPathFrom(id: string, startPixel: Vec2): Vec2[] | null;
  },
): void {
  for (const entry of registry.getAllPathEntries()) {
    rerouteActiveEnemies(waveManager, entry.pixels, entry.id, registry);
  }
}
